import React, { useEffect, useRef, useState } from "react";
import { getSettings, updateSettings, pushTest } from "../lib/api";
import { pushSupported, isPushEnabled, enablePush, disablePush } from "../lib/push";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Cloud, ChevronRight, Building2, BellRing, Upload, Trash2, Send, Clock, ShieldAlert, KeyRound, Siren, ShieldCheck } from "lucide-react";

const DIGEST_TYPES = [
  { id: "certificate", label: "Certificate expiry", icon: ShieldAlert },
  { id: "license", label: "License renewals", icon: KeyRound },
  { id: "e911", label: "E911 compliance gaps", icon: Siren },
  { id: "gate", label: "Runbook gate approvals", icon: ShieldCheck },
];

// Convert a stored UTC hour into a "HH:MM" local time string for the picker.
const utcHourToLocal = (h) => {
  const d = new Date(); d.setUTCHours(h, 0, 0, 0);
  return `${String(d.getHours()).padStart(2, "0")}:00`;
};
// Convert a "HH:MM" local time string into the equivalent UTC hour.
const localTimeToUtcHour = (t) => {
  const [hh, mm] = (t || "08:00").split(":").map(Number);
  const d = new Date(); d.setHours(hh, mm || 0, 0, 0);
  return d.getUTCHours();
};

export default function Settings() {
  const [brand, setBrand] = useState({ company_name: "", tagline: "", logo: "" });
  const [digest, setDigest] = useState({ digest_enabled: true, digest_hour_utc: 8, digest_types: DIGEST_TYPES.map((t) => t.id) });
  const [saving, setSaving] = useState(false);
  const [pushOn, setPushOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    getSettings().then((d) => {
      setBrand({ company_name: d.company_name || "", tagline: d.tagline || "", logo: d.logo || "" });
      setDigest({
        digest_enabled: d.digest_enabled !== false,
        digest_hour_utc: d.digest_hour_utc ?? 8,
        digest_types: d.digest_types || DIGEST_TYPES.map((t) => t.id),
      });
    }).catch(() => {});
    if (pushSupported()) isPushEnabled().then(setPushOn).catch(() => {});
  }, []);

  const saveDigest = async (next) => {
    setDigest(next);
    try { await updateSettings(next); toast.success("Digest preferences saved"); }
    catch { toast.error("Save failed"); }
  };
  const toggleType = (id) => {
    const has = digest.digest_types.includes(id);
    const types = has ? digest.digest_types.filter((t) => t !== id) : [...digest.digest_types, id];
    saveDigest({ ...digest, digest_types: types });
  };

  const saveBrand = async (next) => {
    setSaving(true);
    try { const d = await updateSettings(next); setBrand({ company_name: d.company_name || "", tagline: d.tagline || "", logo: d.logo || "" }); toast.success("Branding saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 800 * 1024) { toast.error("Please use an image under 800KB"); return; }
    const reader = new FileReader();
    reader.onload = () => saveBrand({ ...brand, logo: reader.result });
    reader.readAsDataURL(file);
  };

  const togglePush = async () => {
    setBusy(true);
    try {
      if (pushOn) { await disablePush(); setPushOn(false); toast.info("Closed-app push disabled"); }
      else { await enablePush(); setPushOn(true); toast.success("Closed-app push enabled on this device"); }
    } catch (e) { toast.error(e.message || "Could not change push"); }
    setBusy(false);
  };

  const sendTest = async () => {
    try { const r = await pushTest(); toast.success(r.sent ? `Test sent to ${r.sent} device(s)` : "No subscribed devices yet"); }
    catch { toast.error("Test failed"); }
  };

  return (
    <div>
      <header className="h-16 border-b border-zinc-800 flex items-center px-5 sm:px-8 bg-zinc-950/60 backdrop-blur lg:sticky lg:top-0 z-10">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-2">
          <span className="text-zinc-600">~/</span> settings <ChevronRight className="h-3 w-3" /> branding &amp; notifications
        </span>
      </header>

      <div className="p-5 sm:p-8 max-w-2xl">
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-white tracking-tight">Settings</h1>
        <p className="text-zinc-400 mt-3">Brand your exported PDFs and control device notifications.</p>

        {/* Branding */}
        <div className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-5" data-testid="branding-card">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-blue-400 font-semibold">
            <Building2 className="h-4 w-4" /> Client Branding for PDFs
          </div>
          <p className="text-[13px] text-zinc-500 mt-1">Your logo and company name appear on every exported PDF (audit, scoreboard, capabilities).</p>

          <div className="mt-4 flex items-center gap-4">
            {brand.logo ? (
              <img src={brand.logo} alt="logo" data-testid="brand-logo-preview" className="h-16 w-16 rounded-md object-contain border border-zinc-700 bg-white/5" />
            ) : (
              <div className="h-16 w-16 rounded-md border border-dashed border-zinc-700 flex items-center justify-center"><Cloud className="h-6 w-6 text-zinc-600" /></div>
            )}
            <div className="flex gap-2">
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="logo-file-input" />
              <button onClick={() => fileRef.current?.click()} data-testid="upload-logo-btn" className="flex items-center gap-1.5 text-[12px] font-semibold px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white">
                <Upload className="h-4 w-4" /> {brand.logo ? "Replace logo" : "Upload logo"}
              </button>
              {brand.logo && (
                <button onClick={() => saveBrand({ ...brand, logo: "" })} data-testid="remove-logo-btn" className="flex items-center gap-1.5 text-[12px] px-3 py-2 rounded-md border border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                  <Trash2 className="h-4 w-4" /> Remove
                </button>
              )}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Company name</label>
              <Input data-testid="company-name-input" defaultValue={brand.company_name}
                onBlur={(e) => { if (e.target.value !== brand.company_name) saveBrand({ ...brand, company_name: e.target.value }); }}
                placeholder="e.g. Acme Networks" className="h-9 bg-zinc-950 border-zinc-800 text-sm mt-1" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Tagline (optional)</label>
              <Input data-testid="tagline-input" defaultValue={brand.tagline}
                onBlur={(e) => { if (e.target.value !== brand.tagline) saveBrand({ ...brand, tagline: e.target.value }); }}
                placeholder="e.g. Managed UC Services" className="h-9 bg-zinc-950 border-zinc-800 text-sm mt-1" />
            </div>
          </div>
          {saving && <div className="text-[11px] text-zinc-500 mt-2">Saving…</div>}
        </div>

        {/* Push */}
        <div className="mt-5 rounded-lg border border-zinc-800 bg-zinc-900 p-5" data-testid="push-card">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-blue-400 font-semibold">
            <BellRing className="h-4 w-4" /> Closed-App Push Notifications
          </div>
          <p className="text-[13px] text-zinc-500 mt-1">
            Get alerts on this device even when the app is fully closed — a daily sweep pushes expiring certs/licenses and E911 gaps.
            {!pushSupported() && " (Not supported on this browser — install the app first.)"}
          </p>
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <button onClick={togglePush} disabled={busy || !pushSupported()} data-testid="toggle-push-btn"
              className={`flex items-center gap-2 text-[12px] font-semibold px-3.5 py-2 rounded-md text-white disabled:opacity-50 ${pushOn ? "bg-zinc-700 hover:bg-zinc-600" : "bg-blue-600 hover:bg-blue-700"}`}>
              <BellRing className="h-4 w-4" /> {busy ? "Working…" : pushOn ? "Disable on this device" : "Enable on this device"}
            </button>
            {pushOn && (
              <button onClick={sendTest} data-testid="send-test-push-btn" className="flex items-center gap-2 text-[12px] px-3.5 py-2 rounded-md border border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                <Send className="h-4 w-4" /> Send test push
              </button>
            )}
            <span data-testid="push-status" className={`text-[12px] ${pushOn ? "text-emerald-400" : "text-zinc-500"}`}>
              {pushOn ? "● Enabled on this device" : "○ Not enabled here"}
            </span>
          </div>
          <p className="text-[11px] text-zinc-600 mt-3">On iPhone/iPad, install the app to your Home Screen first (iOS 16.4+), then enable push here.</p>
        </div>

        {/* Daily digest options */}
        <div className="mt-5 rounded-lg border border-zinc-800 bg-zinc-900 p-5" data-testid="digest-card">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-blue-400 font-semibold">
            <Clock className="h-4 w-4" /> Daily Digest Options
          </div>
          <p className="text-[13px] text-zinc-500 mt-1">Choose when the daily alert push goes out and which alert types it includes.</p>

          <div className="mt-4 flex items-center justify-between gap-3 rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2.5">
            <span className="text-[13px] text-zinc-300">Send a daily digest push</span>
            <button onClick={() => saveDigest({ ...digest, digest_enabled: !digest.digest_enabled })} data-testid="digest-enabled-toggle"
              className={`relative h-6 w-11 rounded-full transition-colors ${digest.digest_enabled ? "bg-blue-600" : "bg-zinc-700"}`}>
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${digest.digest_enabled ? "translate-x-5" : "translate-x-0.5"}`} />
            </button>
          </div>

          <div className={`mt-4 ${digest.digest_enabled ? "" : "opacity-40 pointer-events-none"}`}>
            <div className="flex items-center gap-3">
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Send at</label>
              <input
                type="time"
                step="3600"
                data-testid="digest-time-input"
                value={utcHourToLocal(digest.digest_hour_utc)}
                onChange={(e) => saveDigest({ ...digest, digest_hour_utc: localTimeToUtcHour(e.target.value) })}
                className="h-9 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-sm text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <span className="text-[11px] text-zinc-600">your local time (sent {String(digest.digest_hour_utc).padStart(2, "0")}:00 UTC)</span>
            </div>

            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-2">Include these alerts</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {DIGEST_TYPES.map((t) => {
                  const on = digest.digest_types.includes(t.id);
                  return (
                    <button key={t.id} onClick={() => toggleType(t.id)} data-testid={`digest-type-${t.id}`}
                      className={`flex items-center gap-2.5 rounded-md border px-3 py-2 text-left transition-colors ${on ? "border-blue-500/40 bg-blue-500/10" : "border-zinc-800 bg-zinc-950"}`}>
                      <span className={`h-5 w-5 rounded flex items-center justify-center shrink-0 border ${on ? "bg-blue-600 border-blue-600" : "border-zinc-600"}`}>
                        {on && <svg viewBox="0 0 12 12" className="h-3 w-3 text-white"><path fill="currentColor" d="M4.5 8.5L2 6l-.9.9L4.5 10 11 3.5 10.1 2.6z"/></svg>}
                      </span>
                      <t.icon className={`h-4 w-4 ${on ? "text-blue-300" : "text-zinc-500"}`} />
                      <span className={`text-[13px] ${on ? "text-zinc-200" : "text-zinc-400"}`}>{t.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
